import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useNavigate, Link } from 'react-router-dom';
import { getLogoPath, getCollegeInitials } from '../utils/collegeUtils';
import './AccountPage.css';

export default function AccountPage() {
  const [loading, setLoading] = useState(true);
  const [userDetails, setUserDetails] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [error, setError] = useState(null);
  const [statusFixed, setStatusFixed] = useState(false);
  const [leaderboardRank, setLeaderboardRank] = useState(null);
  const [collegeRank, setCollegeRank] = useState(null);
  const [committedCollege, setCommittedCollege] = useState(null);
  const [yearRank, setYearRank] = useState(null);
  const [collegeCommittedRank, setCollegeCommittedRank] = useState(null);
  const [showDevTools, setShowDevTools] = useState(false);
  const [devEmail, setDevEmail] = useState('');
  const [venmoUsername, setVenmoUsername] = useState('');
  const [venmoConnected, setVenmoConnected] = useState(false);
  const [venmoSaving, setVenmoSaving] = useState(false);
  const navigate = useNavigate();

  // Helper function to render college logo
  const renderCollegeLogo = (collegeName) => {
    const logoPath = getLogoPath(collegeName);
    
    return (
      <div className="college-logo-container">
        {logoPath ? (
          <img
            src={logoPath}
            alt={`${collegeName} logo`}
            className="college-logo"
            onError={(e) => {
              const fallback = e.target.nextElementSibling;
              if (fallback) fallback.style.display = 'inline-flex';
              e.target.style.display = 'none';
              e.target.onerror = null;
            }}
          />
        ) : null}
        <span 
          className="college-logo-fallback"
          style={{ display: logoPath ? 'none' : 'inline-flex' }}
        >
          {getCollegeInitials(collegeName)}
        </span>
      </div>
    );
  };

  useEffect(() => {
    async function fetchUserData() {
      setLoading(true);
      setError(null);
      
      try {
        // Get current session
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) throw sessionError;
        
        if (!session) {
          navigate('/login');
          return;
        }
        
        const user = session.user;
        
        // Check if user is a developer
        if (user.email) {
          checkIfDeveloper(user.email);
        }
        
        // Get user details from users table
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('*')
          .eq('id', user.id)
          .single();
          
        if (userError && userError.code !== 'PGRST116') throw userError;
        
        // Get user's submitted profile(s)
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });
          
        if (profileError) throw profileError;
        
        // If multiple profiles exist, take the most recent one (already sorted by created_at desc)
        const mostRecentProfile = profileData && profileData.length > 0 ? profileData[0] : null;
        setUserProfile(mostRecentProfile);
        
        // If user has a profile, fetch their leaderboard rank, year rank, and college ranking
        if (mostRecentProfile) {
          fetchLeaderboardRanking(mostRecentProfile.id, mostRecentProfile.rating);
          
          // Also fetch ranking among same year applicants
          if (mostRecentProfile.class_year) {
            fetchYearRanking(mostRecentProfile.rating, mostRecentProfile.class_year);
          }
          
          // Check if the user has committed to a college
          if (mostRecentProfile.college_results) {
            const committedEntry = Object.entries(mostRecentProfile.college_results).find(
              ([_, status]) => status === 'Committed'
            );
            
            if (committedEntry) {
              const [collegeName] = committedEntry;
              setCommittedCollege(collegeName);
              
              // Fetch both college rankings
              if (mostRecentProfile.class_year) {
                fetchCollegeRanking(mostRecentProfile.rating, collegeName, mostRecentProfile.class_year);
                fetchCollegeCommittedRanking(mostRecentProfile.rating, collegeName, mostRecentProfile.class_year);
              }
            }
          }
        }
        
        // If user has a profile but the submitted_profile flag is false, set it to true
        if (mostRecentProfile && (!userData || !userData.submitted_profile)) {
          // Update the user's submitted_profile flag
          const { error: updateError } = await supabase
            .from('users')
            .update({ submitted_profile: true })
            .eq('id', user.id);
            
          if (updateError) {
            console.error('Error updating user profile status:', updateError);
          } else {
            console.log('Fixed user profile submission status.');
          }
          
          // Update local state to show as submitted
          setUserDetails({
            id: user.id,
            email: user.email,
            lastSignIn: user.last_sign_in_at,
            submitted: true,
            eduEmail: userData?.is_edu || false,
          });
          
          // Set Venmo state
          if (userData?.venmo_username) {
            setVenmoUsername(userData.venmo_username);
            setVenmoConnected(true);
          }
        } else {
          setUserDetails({
            id: user.id,
            email: user.email,
            lastSignIn: user.last_sign_in_at,
            submitted: userData?.submitted_profile || (mostRecentProfile ? true : false), // Consider profile exists = submitted
            eduEmail: userData?.is_edu || false,
          });
          
          // Set Venmo state
          if (userData?.venmo_username) {
            setVenmoUsername(userData.venmo_username);
            setVenmoConnected(true);
          }
        }
        
      } catch (err) {
        console.error('Error fetching user data:', err);
        setError(err.message || 'Failed to load account data');
      } finally {
        setLoading(false);
      }
    }
    
    fetchUserData();
  }, [navigate]);
  
  const fetchLeaderboardRanking = async (profileId, rating) => {
    if (!profileId || !rating) return;
    
    try {
      // Count how many profiles have a higher rating than the user
      const { count: higherRanked, error: rankError } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true })
        .gt('rating', rating);
        
      if (rankError) {
        console.error('Error fetching rank data:', rankError);
        return;
      }
      
      // Calculate rank (add 1 because ranks start at 1, not 0)
      const rank = higherRanked + 1;
      setLeaderboardRank(rank);
      
    } catch (err) {
      console.error('Error calculating leaderboard rank:', err);
    }
  };
  
  const fetchCollegeRanking = async (rating, collegeName, classYear) => {
    if (!rating || !collegeName || !classYear) return;
    
    try {
      // Find profiles where the user committed to the same college in the same year
      const { data: collegeProfiles, error: collegeProfilesError } = await supabase
        .from('profiles')
        .select('rating')
        .eq('class_year', classYear)
        .contains('college_results', { [collegeName]: 'Committed' })
        .order('rating', { ascending: false });
        
      if (collegeProfilesError) {
        console.error('Error fetching college profiles:', collegeProfilesError);
        return;
      }
      
      if (!collegeProfiles || collegeProfiles.length === 0) {
        console.log('No other students committed to this college in this year');
        return;
      }
      
      // Find the user's position in the sorted list
      const position = collegeProfiles.findIndex(profile => profile.rating <= rating) + 1;
      
      // If the user's rating is lower than everyone else's, they'll be at the end
      const rank = position > 0 ? position : collegeProfiles.length + 1;
      
      setCollegeRank({
        rank,
        total: collegeProfiles.length
      });
      
    } catch (err) {
      console.error('Error calculating college ranking:', err);
    }
  };
  
  const fetchYearRanking = async (rating, classYear) => {
    if (!rating || !classYear) return;
    
    try {
      // Count how many profiles in the same year have a higher rating
      const { count: higherRanked, error: rankError } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true })
        .eq('class_year', classYear)
        .gt('rating', rating);
        
      // Get total number of profiles in the same year
      const { count: totalInYear, error: totalError } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true })
        .eq('class_year', classYear);
        
      if (rankError || totalError) {
        console.error('Error fetching year rank data:', rankError || totalError);
        return;
      }
      
      // Calculate rank (add 1 because ranks start at 1, not 0)
      const rank = higherRanked + 1;
      setYearRank({
        rank,
        total: totalInYear
      });
      
    } catch (err) {
      console.error('Error calculating year ranking:', err);
    }
  };
  
  const fetchCollegeCommittedRanking = async (rating, collegeName, classYear) => {
    if (!rating || !collegeName || !classYear) return;
    
    try {
      // Find profiles where students committed to the same college in the same year
      const { data: committedProfiles, error: committedError } = await supabase
        .from('profiles')
        .select('rating')
        .eq('class_year', classYear)
        .contains('college_results', { [collegeName]: 'Committed' })
        .order('rating', { ascending: false });
        
      if (committedError) {
        console.error('Error fetching committed profiles:', committedError);
        return;
      }
      
      if (!committedProfiles || committedProfiles.length === 0) {
        console.log(`No other students committed to ${collegeName} in the class of ${classYear}`);
        return;
      }
      
      // Find the user's position in the sorted list
      const position = committedProfiles.findIndex(profile => profile.rating <= rating) + 1;
      
      // If the user's rating is lower than everyone else's, they'll be at the end
      const rank = position > 0 ? position : committedProfiles.length;
      
      setCollegeCommittedRank(rank);
      
    } catch (err) {
      console.error('Error calculating college committed ranking:', err);
    }
  };
  
  const handleSignOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      navigate('/');
    } catch (err) {
      console.error('Error signing out:', err);
      setError('Failed to sign out. Please try again.');
    }
  };

  const repairProfileStatus = async () => {
    if (!userDetails || !userProfile) return;
    
    if (userProfile && !userDetails.submitted) {
      try {
        console.log("Repairing user submission status...");
        const { error } = await supabase
          .from('users')
          .update({ submitted_profile: true })
          .eq('id', userDetails.id);
          
        if (error) throw error;
        
        setUserDetails({
          ...userDetails,
          submitted: true
        });
        
        setStatusFixed(true);
        console.log("User submission status repaired successfully");
      } catch (err) {
        console.error("Error repairing profile status:", err);
      }
    }
  };

  useEffect(() => {
    if (userProfile && !userDetails?.submitted && !statusFixed) {
      repairProfileStatus();
    }
  }, [userProfile, userDetails]);

  const getRankingText = () => {
    if (leaderboardRank === null) return 'Calculating...';
    return <span className="rank-value">{leaderboardRank}</span>;
  };
  
  const getCollegeRankingText = () => {
    if (!collegeRank || !committedCollege) return null;
    
    return (
      <div className="rank-container">
        <span className="rank-label">{committedCollege} Admits:</span>
        <span className="rank-value">{collegeRank.rank}</span>
      </div>
    );
  };

  const getYearRankingText = () => {
    if (!yearRank || !userProfile?.class_year) return null;
    
    return (
      <div className="rank-container">
        <span className="rank-label">Class of {userProfile.class_year}:</span>
        <span className="rank-value">{yearRank.rank}</span>
      </div>
    );
  };

  const getCollegeCommittedRankText = () => {
    if (!collegeCommittedRank || !committedCollege || !userProfile?.class_year) return null;
    
    return (
      <div className="rank-container">
        <span className="rank-label">{committedCollege} '{userProfile.class_year.toString().slice(-2)} Commits:</span>
        <span className="rank-value">{collegeCommittedRank}</span>
      </div>
    );
  };

  const checkIfDeveloper = async (userEmail) => {
    if (userEmail === 'mostafatafr@gmail.com') {
      setShowDevTools(true);
      setDevEmail(userEmail);
    }
  };

  const forceNewSubmission = async () => {
    try {
      // No need to check session here, the button only shows if logged in
      
      // 1. Set the flag in localStorage to tell SubmitForm to force new mode
      localStorage.setItem('forceNewSubmission', 'true');
      console.log("Dev Tool: Set forceNewSubmission flag in localStorage.");
      
      // 2. Navigate to the submission form
      navigate('/submit');
      
    } catch (error) {
      // Catch potential localStorage errors (though unlikely)
      console.error("Error setting forceNewSubmission flag:", error);
      alert("An error occurred while trying to enable new submission.");
    }
  };

  const deleteProfile = async () => {
    if (!userProfile) return;
    
    if (!window.confirm("Are you sure you want to delete this profile? This action cannot be undone.")) {
      return;
    }
    
    try {
      // Delete the profile from the database
      const { error: deleteError } = await supabase
        .from('profiles')
        .delete()
        .eq('id', userProfile.id);
        
      if (deleteError) {
        console.error("Failed to delete profile:", deleteError);
        alert("Failed to delete profile. Please try again.");
        return;
      }
      
      // Reset the user's submission status
      const { error: userUpdateError } = await supabase
        .from('users')
        .update({ submitted_profile: false })
        .eq('id', userDetails.id);
        
      if (userUpdateError) {
        console.error("Failed to update user status after deletion:", userUpdateError);
      }
      
      // Clear local profile data
      setUserProfile(null);
      
      // Update userDetails to reflect the change
      setUserDetails({
        ...userDetails,
        submitted: false
      });
      
      alert("Profile deleted successfully. You can now submit a new profile.");
      
    } catch (error) {
      console.error("Error in deleteProfile:", error);
      alert("An error occurred while deleting the profile.");
    }
  };

  const forceRepairDatabase = async () => {
    if (!userProfile) {
      alert("No profile found to repair!");
      return;
    }
    
    if (!window.confirm("This will attempt to repair database inconsistencies. Continue?")) {
      return;
    }
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session || !session.user) {
        alert("No active session found. Please log in again.");
        return;
      }
      
      // 1. Update the profile's updated_at field to force a change
      const { error: profileUpdateError } = await supabase
        .from('profiles')
        .update({ 
          updated_at: new Date().toISOString(),
          // Optionally re-save some critical fields
          essay: userProfile.essay,
          extracurriculars: userProfile.extracurriculars
        })
        .eq('id', userProfile.id);
        
      if (profileUpdateError) {
        throw new Error(`Profile update failed: ${profileUpdateError.message}`);
      }
      
      // 2. Ensure users table has correct submission status
      const { error: userUpdateError } = await supabase
        .from('users')
        .update({ 
          submitted_profile: true,
          updated_at: new Date().toISOString()
        })
        .eq('id', session.user.id);
        
      if (userUpdateError) {
        throw new Error(`User update failed: ${userUpdateError.message}`);
      }
      
      alert("Database repair completed successfully!");
      
      // Reload the page to refresh all data
      window.location.reload();
      
    } catch (error) {
      console.error("Error in database repair:", error);
      alert(`Repair failed: ${error.message}`);
    }
  };

  const handleVenmoConnect = async () => {
    if (!venmoUsername.trim()) {
      alert('Please enter a valid Venmo username');
      return;
    }

    setVenmoSaving(true);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Save Venmo username to user profile
      const { error } = await supabase
        .from('users')
        .update({ venmo_username: venmoUsername.trim() })
        .eq('id', user.id);

      if (error) throw error;

      setVenmoConnected(true);
      alert('Venmo account connected successfully!');
      
    } catch (error) {
      console.error('Error connecting Venmo:', error);
      alert(`Failed to connect Venmo: ${error.message}`);
    } finally {
      setVenmoSaving(false);
    }
  };

  const handleVenmoDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect your Venmo account?')) {
      return;
    }

    setVenmoSaving(true);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Remove Venmo username from user profile
      const { error } = await supabase
        .from('users')
        .update({ venmo_username: null })
        .eq('id', user.id);

      if (error) throw error;

      setVenmoUsername('');
      setVenmoConnected(false);
      alert('Venmo account disconnected successfully!');
      
    } catch (error) {
      console.error('Error disconnecting Venmo:', error);
      alert(`Failed to disconnect Venmo: ${error.message}`);
    } finally {
      setVenmoSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="account-page loading">
        <div className="container">
          <h2>Loading account information...</h2>
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="account-page error">
        <div className="container">
          <h2>Error</h2>
          <p className="error-message">{error}</p>
          <button onClick={() => window.location.reload()} className="button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="account-page">
      <div className="container">
        <div className="account-header">
          <h2>Your Account</h2>
          <button onClick={handleSignOut} className="button button-secondary">
            Sign Out
          </button>
        </div>

        <div className="account-section">
          <h3>Account Information</h3>
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">Email:</span>
              <span className="info-value">{userDetails?.email || 'N/A'}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Account Type:</span>
              <span className="info-value">
                {userDetails?.eduEmail ? (
                  <span className="tag edu-tag">EDU Email</span>
                ) : (
                  <span className="tag standard-tag">Standard</span>
                )}
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">Last Sign In:</span>
              <span className="info-value">
                {userDetails?.lastSignIn
                  ? new Date(userDetails.lastSignIn).toLocaleString()
                  : 'N/A'}
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">Profile Status:</span>
              <span className="info-value">
                {userDetails?.submitted ? (
                  <span className="tag success-tag">Submitted</span>
                ) : (
                  <span className="tag pending-tag">Not Submitted</span>
                )}
              </span>
            </div>
          </div>
        </div>

        <div className="account-section">
          <h3>Payment Settings</h3>
          <div className="payment-section">
            <div className="payment-method">
              <div className="payment-header">
                <div className="payment-icon">
                  <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                    <path d="M19.5 3H4.5C3.67 3 3 3.67 3 4.5v15c0 .83.67 1.5 1.5 1.5h15c.83 0 1.5-.67 1.5-1.5v-15c0-.83-.67-1.5-1.5-1.5zM12 8.5c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5-1.5-.67-1.5-1.5.67-1.5 1.5-1.5zm-2.5 7c-.28 0-.5-.22-.5-.5s.22-.5.5-.5h5c.28 0 .5.22.5.5s-.22.5-.5.5h-5z"/>
                  </svg>
                </div>
                <div className="payment-info">
                  <h4>Venmo Account</h4>
                  <p>Connect your Venmo to receive payments when students unlock your essays</p>
                </div>
              </div>
              
              {venmoConnected ? (
                <div className="venmo-connected">
                  <div className="connected-info">
                    <span className="tag success-tag">Connected</span>
                    <span className="venmo-username">@{venmoUsername}</span>
                  </div>
                  <button 
                    onClick={handleVenmoDisconnect}
                    className="button button-secondary"
                    disabled={venmoSaving}
                  >
                    {venmoSaving ? 'Disconnecting...' : 'Disconnect'}
                  </button>
                </div>
              ) : (
                                  <div className="venmo-form">
                    <div className="form-group">
                      <label htmlFor="venmo-username">Venmo Username</label>
                      <input
                        id="venmo-username"
                        type="text"
                        value={venmoUsername}
                        onChange={(e) => setVenmoUsername(e.target.value)}
                        placeholder="your-username"
                        className="form-input"
                        disabled={venmoSaving}
                      />
                      <small className="form-hint">
                        Enter your Venmo username without the @ symbol
                      </small>
                    </div>
                  <button 
                    onClick={handleVenmoConnect}
                    className="button button-primary"
                    disabled={venmoSaving || !venmoUsername.trim()}
                  >
                    {venmoSaving ? 'Connecting...' : 'Connect Venmo'}
                  </button>
                </div>
              )}
            </div>
            
            <div className="payment-note">
              <p>💰 <strong>How payments work:</strong> When students unlock your essays, you'll earn money based on your essay quality and demand. We'll send payments to your connected Venmo account weekly.</p>
            </div>
          </div>
        </div>

        {userProfile && (
          <div className="account-section">
            <h3>Your Profile</h3>
            <div className="profile-card">
              <div className="profile-header">
                <h4>Class of {userProfile.class_year}</h4>
                <div className="rating-badge">
                  <span className="rating-label">Rating:</span>
                  <span className="rating-value">
                    {userProfile.rating !== null && userProfile.rating !== undefined 
                      ? Math.round(userProfile.rating) 
                      : 1200 /* Default rating */}
                  </span>
                </div>
              </div>

              {(leaderboardRank !== null || yearRank || collegeRank || collegeCommittedRank) && (
                <div className="ranking-banner">
                  <div className="ranking-content">
                    <div className="ranking-section">
                      <span className="rank-label">Global Rank:</span>
                      <div className="rank-value">{getRankingText()}</div>
                    </div>
                    
                    {yearRank && (
                      <div className="ranking-section">
                        <span className="rank-label">Class of {userProfile.class_year}:</span>
                        <span className="rank-value">{yearRank.rank}</span>
                      </div>
                    )}
                    
                    {collegeRank && committedCollege && (
                      <div className="ranking-section">
                        <span className="rank-label">{committedCollege} Admits:</span>
                        <span className="rank-value">{collegeRank.rank}</span>
                      </div>
                    )}
                    
                    {collegeCommittedRank && committedCollege && (
                      <div className="ranking-section">
                        <span className="rank-label">{committedCollege} '{userProfile.class_year.toString().slice(-2)} Commits:</span>
                        <span className="rank-value">{collegeCommittedRank}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="profile-details">
                <div className="profile-stats">
                  <div className="stat-item">
                    <span className="stat-label">GPA:</span>
                    <span className="stat-value">
                      {userProfile.gpa ? userProfile.gpa.toFixed(2) : 'N/A'}
                    </span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">SAT:</span>
                    <span className="stat-value">
                      {userProfile.sat_score || 'N/A'}
                    </span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">ACT:</span>
                    <span className="stat-value">
                      {userProfile.act_score || 'N/A'}
                    </span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">APs Taken:</span>
                    <span className="stat-value">
                      {userProfile.ap_num_taken || 'N/A'}
                    </span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">AP 5s:</span>
                    <span className="stat-value">
                      {userProfile.ap_num_fives || 'N/A'}
                    </span>
                  </div>
                </div>

                {userProfile.extracurriculars && userProfile.extracurriculars.length > 0 && (
                  <div className="profile-section">
                    <h5>Extracurriculars</h5>
                    <ul className="ec-list">
                      {userProfile.extracurriculars.map((ec, index) => (
                        <li key={index}>{ec}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {userProfile.college_results && Object.keys(userProfile.college_results).length > 0 && (
                  <div className="profile-section">
                    <h5>College Results</h5>
                    <div className="college-results">
                      {Object.entries(userProfile.college_results).map(([college, status]) => (
                        <div key={college} className={`college-result ${status.toLowerCase()}`}>
                          {renderCollegeLogo(college)}
                          <div className="college-info">
                            <span className="college-name">{college}</span>
                            <span className="college-status">{status}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {userProfile.supplemental_essays && (
                  <div className="profile-section">
                    <h5>Supplemental Essays</h5>
                    {userProfile.supplemental_essays.schools ? (
                      // New multi-school format
                      <div>
                        {Object.entries(userProfile.supplemental_essays.schools).map(([school, schoolData]) => (
                          <div key={school} className="supplemental-school-item">
                            {renderCollegeLogo(school)}
                            <div className="college-info">
                              <span className="college-name">{school}</span>
                              <span className="essay-count">
                                {Object.keys(schoolData.essays || {}).length} Essays
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      // Legacy single-school format
                      <div className="supplemental-school-item">
                        {renderCollegeLogo(userProfile.supplemental_essays.college)}
                        <div className="college-info">
                          <span className="college-name">
                            {userProfile.supplemental_essays.college}
                          </span>
                          <span className="essay-count">
                            {Object.keys(userProfile.supplemental_essays.prompts || userProfile.supplemental_essays.essays || {}).length} Essays
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {userProfile && !userDetails?.submitted && (
          <div className="account-section">
            <div className="status-mismatch-alert">
              <p>⚠️ Your profile status needs to be repaired.</p>
              <button 
                onClick={repairProfileStatus}
                className="button button-secondary"
                disabled={statusFixed}
              >
                {statusFixed ? "Status Fixed" : "Repair Status"}
              </button>
            </div>
          </div>
        )}

        {userDetails?.submitted && !userProfile ? (
          <div className="account-section cta-section loading">
            <h3>Loading Your Profile</h3>
            <div className="spinner"></div>
          </div>
        ) : userProfile ? (
          <div className="account-section cta-section">
            <h3>Update Your Profile</h3>
            <p>Want to make changes or add supplemental essays?</p>
            <div className="cta-button-group">
              <button 
                onClick={() => {
                  localStorage.setItem('editProfileId', userProfile.id);
                  console.log(`Setting profile ${userProfile.id} for editing`);
                  navigate('/submit', { state: { editMode: true, profileId: userProfile.id }});
                }}
                className="cta-button edit-profile-button"
              >
                Edit Profile
              </button>
              
              {showDevTools && (
                <div className="dev-tools-container">
                  <h4>Developer Tools</h4>
                  <div className="dev-tools-buttons">
                    <button 
                      onClick={forceNewSubmission}
                      className="dev-button"
                    >
                      Enable New Submission
                    </button>
                    <button 
                      onClick={deleteProfile}
                      className="dev-button delete-button"
                    >
                      Delete Current Profile
                    </button>
                    <button 
                      onClick={forceRepairDatabase}
                      className="dev-button repair-button"
                    >
                      Force Database Repair
                    </button>
                  </div>
                  <p className="dev-note">Developer access: {devEmail}</p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="account-section cta-section">
            <h3>Submit Your Profile</h3>
            <p>You haven't submitted a profile yet. Share your college application details to compare with others!</p>
            <button 
              onClick={() => navigate('/submit')} 
              className="button button-primary"
            >
              Submit Profile
            </button>
          </div>
        )}
      </div>
    </div>
  );
} 